import React, { useRef, useState } from 'react';
import { Plus, X, History, RefreshCw, Coins } from 'lucide-react';
import type { Creation } from '../../types/create';
import { useAuth } from '../../context/AuthContext';
import { BuyCreditsModal } from '../BuyCreditsModal';

interface CreationSidebarProps {
  mobileSidebarOpen: boolean;
  setMobileSidebarOpen: (isOpen: boolean) => void;
  onNewCreation: () => void;
  loading: boolean;
  creations: Creation[];
  selectedCreationId: string | null;
  onSelectCreation: (id: string) => void;
  onRefresh?: () => Promise<void>;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}

export const CreationSidebar: React.FC<CreationSidebarProps> = ({
  mobileSidebarOpen,
  setMobileSidebarOpen,
  onNewCreation,
  loading,
  creations,
  selectedCreationId,
  onSelectCreation,
  onRefresh,
  hasMore,
  onLoadMore,
  loadingMore
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [startY, setStartY] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const THRESHOLD = 60;

  const { credits } = useAuth();
  const [buyModalOpen, setBuyModalOpen] = useState(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (containerRef.current?.scrollTop === 0) {
      setStartY(e.touches[0].clientY);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!startY || containerRef.current?.scrollTop !== 0) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY;
    
    if (diff > 0) {
      // Add resistance
      setPullDistance(Math.min(diff * 0.5, 100));
    }
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        if (hasMore && !loadingMore && onLoadMore) {
          onLoadMore();
        }
      }
    }
  };

  const handleTouchEnd = async () => {
    if (pullDistance > THRESHOLD && onRefresh && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
    setPullDistance(0);
    setStartY(0);
  };

  const handleRefreshClick = async () => {
    if (onRefresh && !isRefreshing && !loading) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  return (
    <>
      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed md:static inset-y-0 left-0 z-50 md:z-auto w-64 bg-gray-900 text-white flex flex-col flex-shrink-0 transition-transform duration-300 ${
        mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      }`}>
        <div className="p-4 flex items-center justify-between gap-2">
          <button 
            onClick={onNewCreation}
            className="flex-1 flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-md transition-colors border border-white/20 min-h-[44px] touch-manipulation"
          >
            <Plus className="w-4 h-4" />
            <span>New Creation</span>
          </button>
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="md:hidden ml-2 p-2 hover:bg-white/10 rounded-md transition-colors"
            aria-label="Close sidebar"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Credits Badge */}
        <div className="px-4 pb-2">
           <div className="bg-gray-800 rounded-lg p-3 flex items-center justify-between border border-gray-700/50">
              <div className="flex items-center gap-2">
                 <div className="bg-yellow-500/10 p-1.5 rounded-full">
                    <Coins className="w-4 h-4 text-yellow-500" />
                 </div>
                 <div>
                    <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider leading-none mb-0.5">Credits</div>
                    <div className="font-bold text-sm text-white leading-none">{credits !== null ? credits : '-'}</div>
                 </div>
              </div>
              <button 
                onClick={() => setBuyModalOpen(true)}
                className="bg-white text-black hover:bg-gray-100 text-xs font-bold px-3 py-1.5 rounded-md transition-colors"
              >
                Buy
              </button>
           </div>
        </div>

        <div 
          ref={containerRef}
          className="flex-1 overflow-y-auto px-2 relative"
          onScroll={handleScroll}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {pullDistance > 0 && (
            <div 
              className="absolute top-0 left-0 right-0 flex items-center justify-center pointer-events-none transition-all duration-200"
              style={{ height: `${pullDistance}px`, opacity: Math.min(pullDistance / THRESHOLD, 1) }}
            >
              <RefreshCw className={`w-5 h-5 text-gray-400 ${pullDistance > THRESHOLD ? 'animate-spin' : ''}`} />
            </div>
          )}

          <div 
            className="transition-transform duration-200"
            style={{ transform: `translateY(${pullDistance}px)` }}
          >
            <div className="px-2 py-2 flex items-center justify-between text-xs font-semibold text-gray-400 uppercase tracking-wider">
              <span>History</span>
              <button 
                onClick={handleRefreshClick}
                disabled={isRefreshing || loading}
                className="p-1 hover:text-white transition-colors"
                title="Refresh history"
              >
                <RefreshCw className={`w-3 h-3 ${isRefreshing || loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            {loading && creations.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">
                {isRefreshing ? 'Refreshing...' : 'Loading...'}
              </div>
            ) : (
              <div className="space-y-1">
                {creations.map((item) => (
                  <button 
                    key={item.id}
                    onClick={() => onSelectCreation(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-3 text-sm rounded-md transition-colors text-left ${
                      selectedCreationId === item.id 
                        ? 'bg-gray-800 text-white' 
                        : 'text-gray-300 hover:bg-gray-800'
                    }`}
                  >
                    {item.transformedImageUrl ? (
                      <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-gray-700 border border-gray-600">
                        <img 
                          src={item.transformedImageUrl} 
                          alt="Creation" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : item.originalImageUrl ? (
                       <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-gray-700 border border-gray-600">
                        <img 
                          src={item.originalImageUrl} 
                          alt="Original" 
                          className="w-full h-full object-cover opacity-60"
                        />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded flex items-center justify-center bg-gray-800 flex-shrink-0 border border-gray-700">
                        <History className="w-5 h-5 opacity-70" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{item.date.toLocaleDateString()}</div>
                      <div className="text-xs text-gray-500 truncate opacity-60">{item.id}</div>
                    </div>
                  </button>
                ))}
                {loadingMore && (
                  <div className="p-4 text-center text-gray-400 text-sm">
                    Loading more...
                  </div>
                )}
                {!loading && creations.length === 0 && !loadingMore && (
                  <div className="p-4 text-sm text-gray-500 text-center">No creations yet</div>
                )}
              </div>
            )}
          </div>
        </div>
      </aside>
      <BuyCreditsModal isOpen={buyModalOpen} onClose={() => setBuyModalOpen(false)} />
    </>
  );
};

